// Glocken-Benachrichtigungszentrum.
//
// Zeigt eine Glocke mit Ungelesen-Punkt. Klick öffnet ein Dropdown mit dem
// Verlauf der ausgelösten Alerts (aus users/{uid}/alertHits, live). Beim Öffnen
// werden die Einträge als gelesen markiert. "Verwalten" passiert separat über
// die Seitenleiste — der Footer-Link ist nur eine bequeme Abkürzung.
//
// Positionierung (Desktop/chip): Das Panel ist FIX am rechten Viewport-Rand
// verankert (right:24, unter der Kopfzeile) — bündig zur Update-Infokarte, statt
// relativ zur Glocke nach links in den Content zu ragen. Auf Mobile (circle)
// bleibt es relativ zur Glocke (dort sitzt sie ohnehin am rechten Rand).
//
// Motion (Emil-Kowalski-Prinzipien): Panel entsteht aus der oberen Kante,
// transform-origin top-right, federt per Spring nach unten auf; Einträge
// gestaffelt; Exit schneller als Enter; prefers-reduced-motion respektiert.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { F, EASE, DUR } from '../styles/theme';
import { Icon, Icons } from './red/Icons';
import { useT } from '../i18n/useT';
import { subscribeAlertHits, markAllHitsRead, deleteAllHits } from '../services/alertHits';

function relTime(iso, t, lang) {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return t('mod_bell_just_now');
  if (min < 60) return t('mod_bell_min_ago', { n: min });
  const std = Math.floor(min / 60);
  if (std < 24) return t('mod_bell_hr_ago', { n: std });
  const tage = Math.floor(std / 24);
  if (tage < 7) return tage === 1 ? t('mod_bell_day_ago', { n: tage }) : t('mod_bell_days_ago', { n: tage });
  try { return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'de-DE', { day: '2-digit', month: 'short' }); }
  catch { return ''; }
}

/**
 * @param {'circle'|'chip'} variant  TopBar=circle (mobil), DesktopShell=chip
 * @param {() => void} onManage      öffnet den vollen Alerts-Screen (Footer-Link)
 */
export default function NotificationBell({ variant = 'circle', onManage }) {
  const { T } = useTheme();
  const { user } = useAuth();
  const { t, lang } = useT();
  const accent = T.signal || T.red || '#E4544A';
  const reduce = useReducedMotion();
  const isChip = variant === 'chip';

  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!user?.uid) { setHits([]); return; }
    return subscribeAlertHits(user.uid, setHits);
  }, [user?.uid]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const unread = hits.filter((h) => !h.read).length;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0 && user?.uid) {
      setTimeout(() => markAllHitsRead(user.uid, hits), 800);
    }
  };

  const btnStyle = isChip
    ? {
        width: 40, height: 40, borderRadius: 10,
        background: 'transparent', border: 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: T.sub, cursor: 'pointer', position: 'relative',
      }
    : {
        width: 38, height: 38, borderRadius: 999,
        background: 'transparent', border: 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: T.sub, cursor: 'pointer', position: 'relative', flexShrink: 0,
      };

  // ── Positionierung des Panels ──────────────────────────────────────────────
  // Desktop: fix am rechten Viewport-Rand (right:24), direkt unter der Kopfzeile
  //          (~74px) — bündig zur Update-Infokarte. Mobile: relativ zur Glocke.
  const panelPos = isChip
    ? { position: 'fixed', top: 80, right: 24 }
    : { position: 'absolute', top: 'calc(100% + 10px)', right: 0 };

  // ── Motion ─────────────────────────────────────────────────────────────────
  const panelVariants = reduce
    ? {
        hidden: { opacity: 0 },
        show:   { opacity: 1, transition: { duration: 0.12 } },
        exit:   { opacity: 0, transition: { duration: 0.1 } },
      }
    : {
        hidden: { opacity: 0, y: -12, scaleY: 0.85, scaleX: 0.98 },
        show: {
          opacity: 1, y: 0, scaleY: 1, scaleX: 1,
          transition: {
            type: 'spring', stiffness: 470, damping: 34, mass: 0.9,
            opacity: { duration: DUR.fast / 1000, ease: 'easeOut' },
            delayChildren: 0.07, staggerChildren: 0.03,
          },
        },
        exit: { opacity: 0, y: -8, scaleY: 0.92, transition: { duration: 0.13, ease: [0.4, 0, 1, 1] } },
      };
  const surfaceHi = T.surfaceHi || 'rgba(255,255,255,0.04)';

  // Einzelne Benachrichtigung löschen — die Karte verschwindet via AnimatePresence.
  const deleteOne = async (h) => {
    if (!user?.uid || !h?.id) return;
    await deleteAllHits(user.uid, hits, [h.id]);
  };

  // Geteilter Panel-Inhalt (Header + Liste + Footer-Aktionen) — identisch für
  // Desktop-Dropdown und Mobile-Bottom-Sheet.
  const content = (
    <>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '15px 18px 13px',
        borderBottom: `1px solid ${T.line}`, flexShrink: 0,
      }}>
        <Icon d={Icons.bell} size={15} stroke={T.text} sw={2.4} />
        <span style={{
          flex: 1, fontFamily: F.display, fontSize: 15, fontWeight: 800,
          letterSpacing: '-0.02em', color: T.text,
        }}>{t('mod_bell_title')}</span>
        {unread > 0 && (
          <span style={{
            fontFamily: F.family, fontSize: 11, fontWeight: 700, color: '#fff',
            background: accent, borderRadius: 999, padding: '2px 8px', lineHeight: 1.4,
          }}>{unread}</span>
        )}
      </div>

      {/* Liste */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {hits.length === 0 ? (
          <div style={{ padding: '40px 24px 44px', textAlign: 'center' }}>
            <div style={{
              width: 46, height: 46, borderRadius: '50%',
              border: `1px solid ${T.line}`, background: surfaceHi,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <Icon d={Icons.bell} size={20} stroke={T.mute} sw={2.4} />
            </div>
            <div style={{ fontFamily: F.family, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
              {t('mod_bell_empty_title')}
            </div>
            <div style={{ fontFamily: F.family, fontSize: 12, color: T.sub, lineHeight: 1.5, maxWidth: 220, margin: '0 auto' }}>
              {t('mod_bell_empty_sub')}
            </div>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {hits.map((h, i) => (
              <motion.div
                key={h.id}
                layout
                exit={{ opacity: 0, x: 56, transition: { duration: DUR.base / 1000, ease: [0.4, 0, 1, 1] } }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 11,
                  padding: '13px 12px 13px 18px',
                  borderTop: i ? `1px solid ${T.lineSoft || T.line}` : 'none',
                  background: h.read ? 'transparent' : surfaceHi,
                }}
              >
                <span style={{
                  marginTop: 6, width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: h.read ? T.line : accent,
                }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontFamily: F.family, fontSize: 13, fontWeight: 700, color: T.text,
                    letterSpacing: '-0.01em', lineHeight: 1.35,
                  }}>{h.title || t('mod_bell_fired')}</div>
                  {h.body && (
                    <div style={{ fontSize: 12, color: T.sub, fontFamily: F.family, marginTop: 2, lineHeight: 1.45 }}>{h.body}</div>
                  )}
                  <div style={{ fontSize: 10.5, color: T.mute, fontFamily: F.family, marginTop: 4, letterSpacing: '0.01em' }}>{relTime(h.ts, t, lang)}</div>
                </div>
                {/* X — einzelne Benachrichtigung löschen */}
                <button
                  onClick={() => deleteOne(h)}
                  aria-label={t('alert_delete_aria') || 'Löschen'}
                  style={{
                    flexShrink: 0, width: 26, height: 26, marginTop: -1,
                    borderRadius: 999, background: 'none', border: 'none',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: T.mute,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <Icon d={Icons.close} size={13} stroke="currentColor" sw={2.2} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Footer: Alle Alerts verwalten (volle Breite). */}
      {onManage && (
        <button
          onClick={() => { setOpen(false); onManage(); }}
          style={{
            width: '100%', padding: '14px', background: 'none', border: 'none',
            borderTop: `1px solid ${T.line}`, flexShrink: 0,
            color: T.sub, fontFamily: F.family, fontSize: 12.5, fontWeight: 700,
            cursor: 'pointer', letterSpacing: '-0.01em',
            WebkitTapHighlightColor: 'transparent',
          }}
        >{t('mod_bell_manage_all')}</button>
      )}
    </>
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={toggle}
        aria-label={t('mod_bell_title')}
        style={{ ...btnStyle, transition: `color ${DUR.fast}ms ease, transform ${DUR.press}ms ${EASE}` }}
        onMouseEnter={isChip ? (e) => { e.currentTarget.style.color = T.text; e.currentTarget.style.transform = 'scale(1.12)'; } : undefined}
        onMouseLeave={isChip ? (e) => { e.currentTarget.style.color = T.sub; e.currentTarget.style.transform = 'scale(1)'; } : undefined}
        onMouseDown={isChip ? (e) => { e.currentTarget.style.transform = 'scale(0.9)'; } : undefined}
        onMouseUp={isChip ? (e) => { e.currentTarget.style.transform = 'scale(1.12)'; } : undefined}
      >
        <Icon d={Icons.bell} size={isChip ? 20 : 22} stroke="currentColor" sw={2.4} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: isChip ? 7 : 6, right: isChip ? 7 : 6,
            minWidth: 7, height: 7, borderRadius: '50%', background: accent,
            boxShadow: `0 0 0 2px ${T.navBg || T.bg || '#000'}`,
          }} />
        )}
      </button>

      {/* Desktop (chip): Dropdown — relativ zur Glocke, bleibt inline. */}
      <AnimatePresence>
        {open && isChip && (
          <motion.div
            key="bell-dropdown"
            role="menu"
            variants={panelVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            style={{
              ...panelPos, zIndex: 9998, transformOrigin: 'top right',
              width: 340, maxWidth: 'calc(100vw - 48px)',
              maxHeight: 'min(calc(100vh - 110px), 600px)',
              display: 'flex', flexDirection: 'column',
              background: T.surface, border: `1px solid ${T.line}`,
              borderRadius: 16, overflow: 'hidden',
              boxShadow: T.isDark
                ? '0 18px 48px -14px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
                : '0 18px 44px -14px rgba(60,48,28,0.26), 0 3px 10px rgba(60,48,28,0.08)',
            }}
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile (circle): Bottom-Sheet von unten. PFLICHT via Portal an
          document.body — sonst ankert position:fixed an einem transformierten
          Vorfahren (Page-Transition-Wrapper) und das Sheet öffnet oben statt
          unten an der Navbar. */}
      {createPortal(
        <AnimatePresence>
          {open && !isChip && (
            <motion.div
              key="bell-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: DUR.base / 1000 }}
              onClick={() => setOpen(false)}
              style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                zIndex: 2147483000,
              }}
            />
          )}
          {open && !isChip && (
            <motion.div
              key="bell-sheet"
              initial={{ y: '112%' }} animate={{ y: 0 }} exit={{ y: '112%' }}
              transition={{ type: 'spring', stiffness: 420, damping: 40, mass: 0.9 }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              onDragEnd={(_e, info) => { if (info.offset.y > 90 || info.velocity.y > 600) setOpen(false); }}
              style={{
                position: 'fixed', left: 0, right: 0, bottom: 0, margin: '0 auto',
                width: '100%', maxWidth: 480, maxHeight: '74vh',
                display: 'flex', flexDirection: 'column',
                background: T.surface, borderTop: `1px solid ${T.line}`,
                borderRadius: '24px 24px 0 0',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                zIndex: 2147483001, overflow: 'hidden',
                boxShadow: '0 -14px 44px rgba(0,0,0,0.45)',
              }}
            >
              <div style={{ width: 40, height: 4, borderRadius: 999, background: T.line, margin: '12px auto 4px', flexShrink: 0 }} />
              {content}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
